import React, { useMemo, useState, Children } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Toggle } from '../components/ui/Toggle';
import { Input } from '../components/ui/Input';
import { ArrowLeft, Building2, MapPin, Settings, Layers, BarChart3, Home as HomeIcon, Search, Plus, BedDouble, Bath, Ruler, ImageOff, MoreVertical, Check, Info, DollarSign, Edit2, X, Save, Image, FileText, Palette, Upload, Trash2, ChevronDown, ChevronRight, Package, Users, Activity, Monitor, Rocket, ArrowRight, LogOut, LayoutTemplate, Eye, Settings2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { builderCommunities, initialModels, initialCatalogs, initialGlobalOptions, type HouseModel, type ModelRoom } from '../data/optionsData';
import { initialCommunityOptions, type CommunityOption, communityModels } from '../data/communityOptionsData';
type Tab = 'models' | 'options' | 'analytics' | 'settings';
export function CommunityDetailPage() {
  const {
    builderId,
    communityId,
    settingsTab
  } = useParams<{
    builderId: string;
    communityId: string;
    settingsTab: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Role detection: admin routes start with /admin
  const isAdmin = location.pathname.startsWith('/admin');
  // Determine active tab from URL or default to models
  const pathParts = location.pathname.split('/');
  const currentTab = (pathParts[pathParts.length - 1] === communityId ? 'models' : settingsTab ? 'settings' : pathParts[pathParts.length - 1]) as Tab;
  const [searchQuery, setSearchQuery] = useState('');
  // Local state for models to allow editing
  const [localModels, setLocalModels] = useState<HouseModel[]>(initialModels);
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<HouseModel | null>(null);
  // Settings state — derived from URL param, defaults to 'info'
  const settingsSection = settingsTab || 'info';
  const [hasChanges, setHasChanges] = useState(false);
  const [clientLoginEnabled, setClientLoginEnabled] = useState(false);
  // GDN Launcher State
  const [gdnTitle, setGdnTitle] = useState('VIRTUAL EXPERIENCE');
  const [gdnButtonText, setGdnButtonText] = useState('BEGIN TOUR');
  const [gdnExitText, setGdnExitText] = useState('Exit');
  const [gdnPrimaryColor, setGdnPrimaryColor] = useState('#1e293b');
  const [gdnOverlayColor, setGdnOverlayColor] = useState('#0f172a');
  const [gdnOnPrimaryColor, setGdnOnPrimaryColor] = useState('#ffffff');
  // Alternative GDN Layout State
  const [gdnViewMode, setGdnViewMode] = useState<'preview' | 'editor'>('preview');
  const [isGdnDrawerOpen, setIsGdnDrawerOpen] = useState(false);
  // Drag and drop state
  const [dragActive, setDragActive] = useState(false);
  // Options Manager state
  const [communityOptions, setCommunityOptions] = useState<CommunityOption[]>(initialCommunityOptions);
  const [optionsSection, setOptionsSection] = useState<'exterior' | 'interior' | 'model-elevation' | 'model-structural'>('exterior');
  const [optionsModel, setOptionsModel] = useState(communityModels[0]?.name || '');
  const [selectedLvs, setSelectedLvs] = useState<string | null>(null);
  const [selectedVariantSet, setSelectedVariantSet] = useState<string | null>(null);
  const [expandedLvsGroups, setExpandedLvsGroups] = useState<Set<string>>(new Set());
  const community = useMemo(() => builderCommunities.find(c => c.id === communityId) || builderCommunities.find(c => c.name.toLowerCase().replace(/\s+/g, '-') === communityId) || (isAdmin ? builderCommunities[0] : undefined), [communityId, isAdmin]);
  // Filter models based on search query
  const models = useMemo(() => {
    if (!searchQuery) return localModels;
    return localModels.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery, localModels]);
  const handleOpenModal = (model?: HouseModel) => {
    if (model) {
      setEditingModel({
        ...model
      });
    } else {
      // Default new model
      setEditingModel({
        id: `model-${Date.now()}`,
        name: '',
        sqft: 0,
        beds: 0,
        baths: 0,
        stories: 1,
        type: 'Laned Home',
        basePrice: 0,
        description: '',
        location: community?.regionName || '',
        status: 'draft',
        hasImage: false,
        imageUrl: '',
        communities: [communityId || ''],
        assignedCatalogs: [],
        tags: [],
        enabled: true,
        displayProductPrices: false,
        ueAssetPath: '',
        ueLevelName: ''
      });
    }
    setIsModalOpen(true);
  };
  const handleSaveModel = () => {
    if (!editingModel) return;
    setLocalModels(prev => {
      const exists = prev.find(m => m.id === editingModel.id);
      if (exists) {
        return prev.map(m => m.id === editingModel.id ? editingModel : m);
      } else {
        return [...prev, editingModel];
      }
    });
    setIsModalOpen(false);
    setEditingModel(null);
  };
  const handleToggleModel = (modelId: string) => {
    setLocalModels(prev => prev.map(m => m.id === modelId ? {
      ...m,
      status: m.status === 'active' ? 'draft' : 'active'
    } : m));
  };
  const handleAddRoom = () => {
    if (!editingModel) return;
    const newRoom: ModelRoom = {
      id: `room-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: '',
      sqft: 0,
      type: 'Bedroom',
      floor: 'Main'
    };
    setEditingModel({
      ...editingModel,
      rooms: [...(editingModel.rooms || []), newRoom]
    });
  };
  const handleUpdateRoom = (roomId: string, updates: Partial<ModelRoom>) => {
    if (!editingModel) return;
    setEditingModel({
      ...editingModel,
      rooms: (editingModel.rooms || []).map(r => r.id === roomId ? {
        ...r,
        ...updates
      } : r)
    });
  };
  const handleRemoveRoom = (roomId: string) => {
    if (!editingModel) return;
    setEditingModel({
      ...editingModel,
      rooms: (editingModel.rooms || []).filter(r => r.id !== roomId)
    });
  };
  const handleToggleOption = (id: string) => {
    setCommunityOptions(prev => prev.map(opt => opt.id === id ? {
      ...opt,
      enabled: !opt.enabled
    } : opt));
  };
  const handleToggleSubVariant = (optionId: string, subVariantName: string) => {
    setCommunityOptions(prev => prev.map(opt => {
      if (opt.id !== optionId || !opt.subVariants) return opt;
      return {
        ...opt,
        subVariants: opt.subVariants.map(sv => sv.name === subVariantName ? {
          ...sv,
          enabled: !sv.enabled
        } : sv)
      };
    }));
  };
  // Filter and group options
  const filteredOptions = useMemo(() => {
    let filtered = communityOptions.filter(o => o.section === optionsSection);
    if (optionsSection === 'model-elevation' || optionsSection === 'model-structural') {
      filtered = filtered.filter(o => o.modelName === optionsModel);
    }
    return filtered;
  }, [communityOptions, optionsSection, optionsModel]);
  const groupedOptions = useMemo(() => {
    const grouped: Record<string, Record<string, Record<string, CommunityOption[]>>> = {};
    filteredOptions.forEach(opt => {
      if (!grouped[opt.lvs]) grouped[opt.lvs] = {};
      if (!grouped[opt.lvs][opt.variantSet]) grouped[opt.lvs][opt.variantSet] = {};
      if (!grouped[opt.lvs][opt.variantSet][opt.page]) grouped[opt.lvs][opt.variantSet][opt.page] = [];
      grouped[opt.lvs][opt.variantSet][opt.page].push(opt);
    });
    return grouped;
  }, [filteredOptions]);
  // Build nav tree with counts
  const navTree = useMemo(() => {
    const tree: {
      lvs: string;
      count: number;
      variantSets: {
        name: string;
        count: number;
      }[];
    }[] = [];
    Object.entries(groupedOptions).forEach(([lvs, variantSets]) => {
      let lvsCount = 0;
      const vs: {
        name: string;
        count: number;
      }[] = [];
      Object.entries(variantSets).forEach(([vsName, pages]) => {
        let vsCount = 0;
        Object.values(pages).forEach(opts => {
          vsCount += opts.length;
        });
        vs.push({
          name: vsName,
          count: vsCount
        });
        lvsCount += vsCount;
      });
      tree.push({
        lvs,
        count: lvsCount,
        variantSets: vs
      });
    });
    return tree;
  }, [groupedOptions]);
  // Visible options based on side nav selection
  const visibleGroupedOptions = useMemo(() => {
    if (!selectedLvs) return groupedOptions;
    const result: typeof groupedOptions = {};
    if (groupedOptions[selectedLvs]) {
      if (selectedVariantSet && groupedOptions[selectedLvs][selectedVariantSet]) {
        result[selectedLvs] = {
          [selectedVariantSet]: groupedOptions[selectedLvs][selectedVariantSet]
        };
      } else {
        result[selectedLvs] = groupedOptions[selectedLvs];
      }
    }
    return result;
  }, [groupedOptions, selectedLvs, selectedVariantSet]);
  // Reset nav selection when section changes
  const handleSectionChange = (section: typeof optionsSection) => {
    setOptionsSection(section);
    setSelectedLvs(null);
    setSelectedVariantSet(null);
    setExpandedLvsGroups(new Set());
  };
  const toggleLvsExpanded = (lvs: string) => {
    setExpandedLvsGroups(prev => {
      const next = new Set(prev);
      if (next.has(lvs)) {
        next.delete(lvs);
      } else {
        next.add(lvs);
      }
      return next;
    });
  };
  const handleNavSelectLvs = (lvs: string) => {
    if (selectedLvs === lvs && !selectedVariantSet) {
      setSelectedLvs(null);
    } else {
      setSelectedLvs(lvs);
      setSelectedVariantSet(null);
    }
    if (!expandedLvsGroups.has(lvs)) {
      setExpandedLvsGroups(prev => new Set(prev).add(lvs));
    }
  };
  const handleNavSelectVariantSet = (lvs: string, vs: string) => {
    if (selectedLvs === lvs && selectedVariantSet === vs) {
      setSelectedVariantSet(null);
    } else {
      setSelectedLvs(lvs);
      setSelectedVariantSet(vs);
    }
  };
  // Build base paths based on role
  const basePath = isAdmin ? `/admin/communities/${communityId}` : `/${builderId}/communities/${communityId}`;
  const backPath = isAdmin ? '/admin/communities' : `/${builderId}/communities`;
  if (!community) {
    return <div className="p-12 text-center" data-id="element-3772">
        <h2 className="text-xl font-semibold text-slate-900" data-id="element-3773">
          Community not found
        </h2>
        <Button className="mt-4" onClick={() => navigate(backPath)} data-id="element-3774">
          Back to Communities
        </Button>
      </div>;
  }
  const handleTabChange = (tab: Tab) => {
    if (tab === 'models') {
      navigate(basePath);
    } else if (tab === 'settings') {
      navigate(`${basePath}/settings/info`);
    } else {
      navigate(`${basePath}/${tab}`);
    }
  };
  return <div className="space-y-6 max-w-7xl mx-auto pb-12" data-id="element-3775">
      {/* Header */}
      <div className="flex items-start justify-between gap-4" data-id="element-3776">
        <div className="flex items-start gap-4" data-id="element-3777">
          <Button variant="ghost" onClick={() => navigate(backPath)} className="text-slate-500 hover:text-slate-900 px-2 mt-1" data-id="element-3778">
            <ArrowLeft className="h-5 w-5" data-id="element-3779" />
          </Button>
          <div data-id="element-3780">
            <div className="flex items-center gap-3 mb-1" data-id="element-3781">
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight" data-id="element-3782">
                {community.name}
              </h1>
              <Badge variant={community.status === 'active' ? 'success' : 'neutral'} className="capitalize" data-id="element-3783">
                {community.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500" data-id="element-3784">
              <span className="flex items-center gap-1.5" data-id="element-3785">
                <MapPin className="h-4 w-4" data-id="element-3786" />
                {community.regionName}
              </span>
              <span className="flex items-center gap-1.5" data-id="element-3787">
                <HomeIcon className="h-4 w-4" data-id="element-3788" />
                {community.modelCount} Models
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-3789">
          <Button variant="outline" data-id="element-3790">Edit Community</Button>
          <Button variant="ghost" className="px-2 text-slate-500" data-id="element-3791">
            <Settings className="h-5 w-5" data-id="element-3792" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200" data-id="element-3793">
        <nav className="flex gap-6" data-id="element-3794">
          <button onClick={() => handleTabChange('models')} className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${currentTab === 'models' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`} data-id="element-3795">
            <HomeIcon className="h-4 w-4" data-id="element-3796" />
            Models
          </button>
          <button onClick={() => handleTabChange('options')} className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${currentTab === 'options' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`} data-id="element-3797">
            <Layers className="h-4 w-4" data-id="element-3798" />
            Options
          </button>
          <button onClick={() => handleTabChange('analytics')} className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${currentTab === 'analytics' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`} data-id="element-3799">
            <BarChart3 className="h-4 w-4" data-id="element-3800" />
            Analytics
          </button>
          <button onClick={() => handleTabChange('settings')} className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${currentTab === 'settings' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`} data-id="element-3801">
            <Settings className="h-4 w-4" data-id="element-3802" />
            Settings
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="pt-2" data-id="element-3803">
        {/* MODELS TAB */}
        {currentTab === 'models' && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-3804">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3805">
              <div className="relative max-w-md w-full" data-id="element-3806">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-3807" />
                <input type="text" placeholder="Search models..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow" data-id="element-3808" />
              </div>
              {isAdmin && <Button leftIcon={<Plus className="h-4 w-4" data-id="element-3810" />} onClick={() => handleOpenModal()} data-id="element-3809">
                  Add Model
                </Button>}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-id="element-3811">
              {models.map(model => <div key={model.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-row hover:shadow-md transition-shadow group" data-id="element-3812">
                  {/* Left: Image */}
                  <div className="relative w-44 sm:w-56 flex-shrink-0 bg-slate-100" data-id="element-3813">
                    {model.hasImage && model.imageUrl ? <img src={model.imageUrl} alt={model.name} className="w-full h-full object-cover" data-id="element-3814" /> : <div className="w-full h-full flex items-center justify-center text-slate-400 min-h-[160px]" data-id="element-3815">
                        <ImageOff className="h-8 w-8" data-id="element-3816" />
                      </div>}
                    <div className="absolute top-2 right-2" data-id="element-3817">
                      <Badge variant={model.status === 'active' ? 'success' : 'neutral'} className="capitalize text-xs" data-id="element-3818">
                        {model.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Right: Details */}
                  <div className="p-4 flex flex-col flex-1 min-w-0" data-id="element-3819">
                    <div className="flex items-start justify-between gap-2 mb-1" data-id="element-3820">
                      <h3 className="text-lg font-bold text-slate-900 uppercase tracking-wide truncate" data-id="element-3821">
                        {model.name}
                      </h3>
                      {isAdmin && <button onClick={() => handleOpenModal(model)} className="flex-shrink-0 p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100" data-id="element-3822">
                          <Edit2 className="h-4 w-4" data-id="element-3823" />
                        </button>}
                    </div>

                    <div className="flex items-center gap-1.5 text-xs font-semibold text-primary-600 mb-3 tracking-wider" data-id="element-3824">
                      <span data-id="element-3825">{model.sqft} SQ FT</span>
                      <span className="text-slate-300" data-id="element-3826">|</span>
                      <span data-id="element-3827">{model.beds} BEDS</span>
                      <span className="text-slate-300" data-id="element-3828">|</span>
                      <span data-id="element-3829">{model.baths} BATHS</span>
                    </div>

                    <div className="space-y-1.5 text-sm text-slate-500" data-id="element-3830">
                      <div className="flex items-center gap-2" data-id="element-3831">
                        <MapPin className="h-3.5 w-3.5 text-slate-400" data-id="element-3832" />
                        <span data-id="element-3833">{model.location || 'Location not set'}</span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-3834">
                        <Info className="h-3.5 w-3.5 text-slate-400" data-id="element-3835" />
                        <span data-id="element-3836">{model.type}</span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-3837">
                        <DollarSign className="h-3.5 w-3.5 text-slate-400" data-id="element-3838" />
                        <span className="font-semibold text-slate-900" data-id="element-3839">
                          ${(model.basePrice / 1000).toFixed(0)}k
                        </span>
                      </div>
                    </div>

                    {/* Assigned Catalogs */}
                    {model.assignedCatalogs.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-3840">
                        {model.assignedCatalogs.map(cat => <span key={cat} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium bg-primary-50 text-primary-700 rounded border border-primary-100" data-id="element-3841">
                            <Layers className="h-2.5 w-2.5" data-id="element-3842" />
                            {cat}
                          </span>)}
                      </div>}

                    {/* Enable/Disable Toggle */}
                    <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between" data-id="element-3843">
                      <span className="text-xs text-slate-500" data-id="element-3844">
                        Availability
                      </span>
                      <div className="flex items-center gap-2" data-id="element-3845">
                        <Toggle size="sm" checked={model.status === 'active'} onChange={() => handleToggleModel(model.id)} data-id="element-3846" />
                        <span className={`text-xs font-medium ${model.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`} data-id="element-3847">
                          {model.status === 'active' ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>

            {models.length === 0 && <div className="text-center py-12 bg-white rounded-xl border border-slate-200" data-id="element-3848">
                <HomeIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" data-id="element-3849" />
                <h3 className="text-lg font-medium text-slate-900" data-id="element-3850">
                  No models found
                </h3>
                <p className="text-slate-500 mt-1" data-id="element-3851">
                  Try adjusting your search.
                </p>
              </div>}
          </div>}

        {/* OPTIONS TAB */}
        {currentTab === 'options' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 space-y-5" data-id="element-3852">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-3853">
              <div data-id="element-3854">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-3855">
                  Community Options
                </h2>
                <p className="text-sm text-slate-500" data-id="element-3856">
                  Enable or disable options specific to {community.name}.
                </p>
              </div>
            </div>

            {/* Top Section Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-1" data-id="element-3857">
              {[{
            id: 'exterior' as const,
            label: 'Exterior',
            icon: Palette
          }, {
            id: 'interior' as const,
            label: 'Interior',
            icon: HomeIcon
          }, {
            id: 'model-elevation' as const,
            label: 'Model Elevations',
            icon: Layers
          }, {
            id: 'model-structural' as const,
            label: 'Model Structural',
            icon: Building2
          }].map(tab => {
            const TabIcon = tab.icon;
            return <button key={tab.id} onClick={() => handleSectionChange(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${optionsSection === tab.id ? 'bg-primary-50 text-primary-700 ring-1 ring-primary-200' : 'bg-white text-slate-600 hover:bg-slate-50 ring-1 ring-slate-200'}`} data-id="element-3858">
                    <TabIcon className="h-4 w-4" data-id="element-3859" />
                    {tab.label}
                  </button>;
          })}
            </div>

            {/* Model Selector (if applicable) */}
            {(optionsSection === 'model-elevation' || optionsSection === 'model-structural') && <div className="flex items-center gap-3" data-id="element-3860">
                <label className="text-sm font-medium text-slate-700" data-id="element-3861">
                  Model:
                </label>
                <select value={optionsModel} onChange={e => {
            setOptionsModel(e.target.value);
            setSelectedLvs(null);
            setSelectedVariantSet(null);
          }} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white" data-id="element-3862">
                  {communityModels.map(m => <option key={m.name} value={m.name} data-id="element-3863">
                      {m.name}
                    </option>)}
                </select>
              </div>}

            {/* Two-column layout: Side Nav + Content */}
            <div className="flex gap-6 items-start" data-id="element-3864">
              {/* Side Navigation */}
              <div className="w-64 flex-shrink-0 sticky top-4" data-id="element-3865">
                <Card className="p-0 overflow-hidden" data-id="element-3866">
                  <div className="px-4 py-3 border-b border-slate-200 bg-slate-50" data-id="element-3867">
                    <h3 className="text-sm font-semibold text-slate-800" data-id="element-3868">
                      Categories
                    </h3>
                  </div>

                  <div className="py-1" data-id="element-3869">
                    {/* All Categories */}
                    <button onClick={() => {
                  setSelectedLvs(null);
                  setSelectedVariantSet(null);
                }} className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${!selectedLvs ? 'bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-500' : 'text-slate-700 hover:bg-slate-50 border-l-2 border-transparent'}`} data-id="element-3870">
                      <HomeIcon className="h-4 w-4 flex-shrink-0" data-id="element-3871" />
                      <span className="flex-1 text-left" data-id="element-3872">All Categories</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${!selectedLvs ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-3873">
                        {filteredOptions.length}
                      </span>
                    </button>

                    {/* LVS Groups */}
                    {navTree.map(group => <div key={group.lvs} data-id="element-3874">
                        {/* LVS Header */}
                        <button onClick={() => {
                    toggleLvsExpanded(group.lvs);
                    handleNavSelectLvs(group.lvs);
                  }} className={`w-full flex items-center gap-1.5 pl-3 pr-4 py-2.5 text-sm transition-colors ${selectedLvs === group.lvs && !selectedVariantSet ? 'bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-500' : selectedLvs === group.lvs ? 'bg-slate-50 text-slate-900 font-medium border-l-2 border-slate-300' : 'text-slate-700 hover:bg-slate-50 border-l-2 border-transparent'}`} data-id="element-3875">
                          {expandedLvsGroups.has(group.lvs) ? <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" data-id="element-3876" /> : <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" data-id="element-3877" />}
                          <span className="flex-1 text-left" data-id="element-3878">{group.lvs}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${selectedLvs === group.lvs && !selectedVariantSet ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-3879">
                            {group.count}
                          </span>
                        </button>

                        {/* Variant Set Children */}
                        {expandedLvsGroups.has(group.lvs) && <div className="pb-1" data-id="element-3880">
                            {group.variantSets.map(vs => <button key={vs.name} onClick={() => handleNavSelectVariantSet(group.lvs, vs.name)} className={`w-full flex items-center gap-2 pl-10 pr-4 py-2 text-sm transition-colors ${selectedLvs === group.lvs && selectedVariantSet === vs.name ? 'bg-primary-50 text-primary-700 font-medium border-l-2 border-primary-500' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800 border-l-2 border-transparent'}`} data-id="element-3881">
                                <span className="flex-1 text-left truncate" data-id="element-3882">
                                  {vs.name}
                                </span>
                                <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${selectedLvs === group.lvs && selectedVariantSet === vs.name ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-3883">
                                  {vs.count}
                                </span>
                              </button>)}
                          </div>}
                      </div>)}
                  </div>
                </Card>
              </div>

              {/* Main Content */}
              <div className="flex-1 min-w-0" data-id="element-3884">
                <Card className="p-0 overflow-hidden" data-id="element-3885">
                  <div className="divide-y divide-slate-200" data-id="element-3886">
                    {Object.entries(visibleGroupedOptions).map(([lvs, variantSets]) => <div key={lvs} className="bg-white" data-id="element-3887">
                          <div className="px-6 py-3.5 bg-slate-50/80 border-b border-slate-200 sticky top-0 z-10" data-id="element-3888">
                            <h3 className="text-base font-bold text-slate-900" data-id="element-3889">
                              {lvs}
                            </h3>
                          </div>

                          <div className="divide-y divide-slate-100" data-id="element-3890">
                            {Object.entries(variantSets).map(([variantSet, pages]) => <div key={variantSet} className="px-6 py-4" data-id="element-3891">
                                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-3892">
                                    {variantSet}
                                  </h4>

                                  <div className="space-y-5" data-id="element-3893">
                                    {Object.entries(pages).map(([page, options]) => <div key={page} className="space-y-2.5" data-id="element-3894">
                                          {page && page !== 'Default' && <Badge variant="neutral" className="bg-slate-100 text-slate-600 border-none text-xs" data-id="element-3895">
                                              {page}
                                            </Badge>}

                                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5" data-id="element-3896">
                                            {options.map(opt => <div key={opt.id} className="flex items-center justify-between py-2.5 px-3.5 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors bg-white" data-id="element-3897">
                                                <div className="flex-1 min-w-0 pr-3" data-id="element-3898">
                                                  <p className="text-sm font-medium text-slate-900 truncate" title={opt.variant} data-id="element-3899">
                                                    {opt.variant}
                                                  </p>
                                                  {opt.subVariants && opt.subVariants.length > 0 && <div className="mt-1.5 space-y-1.5" data-id="element-3900">
                                                        {opt.subVariants.map(sv => <div key={sv.name} className="flex items-center justify-between text-xs" data-id="element-3901">
                                                              <span className="text-slate-500 truncate pr-2" data-id="element-3902">
                                                                {sv.name}
                                                              </span>
                                                              <Toggle size="sm" checked={sv.enabled} onChange={() => handleToggleSubVariant(opt.id, sv.name)} data-id="element-3903" />
                                                            </div>)}
                                                      </div>}
                                                </div>
                                                <div className="flex-shrink-0" data-id="element-3904">
                                                  <Toggle size="sm" checked={opt.enabled} onChange={() => handleToggleOption(opt.id)} data-id="element-3905" />
                                                </div>
                                              </div>)}
                                          </div>
                                        </div>)}
                                  </div>
                                </div>)}
                          </div>
                        </div>)}

                    {Object.keys(visibleGroupedOptions).length === 0 && <div className="p-12 text-center" data-id="element-3906">
                        <Layers className="h-12 w-12 text-slate-300 mx-auto mb-3" data-id="element-3907" />
                        <h3 className="text-lg font-medium text-slate-900" data-id="element-3908">
                          No options found
                        </h3>
                        <p className="text-slate-500 mt-1" data-id="element-3909">
                          There are no options configured for this section.
                        </p>
                      </div>}
                  </div>
                </Card>
              </div>
            </div>
          </div>}

        {/* ANALYTICS TAB */}
        {currentTab === 'analytics' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-300 max-w-3xl" data-id="element-3910">
            <Card className="p-8 text-center border-dashed border-2 border-slate-200" data-id="element-3911">
              <div className="h-16 w-16 rounded-full bg-purple-50 flex items-center justify-center mx-auto mb-4" data-id="element-3912">
                <BarChart3 className="h-8 w-8 text-purple-500" data-id="element-3913" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-3914">
                Community Analytics
              </h2>
              <p className="text-slate-500 mb-8 max-w-md mx-auto" data-id="element-3915">
                View engagement metrics, popular selections, and revenue data
                for {community.name}.
              </p>
              <Button onClick={() => navigate(isAdmin ? '/admin/analytics' : `/${builderId}/analytics`)} leftIcon={<BarChart3 className="h-4 w-4" data-id="element-3917" />} data-id="element-3916">
                View Full Analytics
              </Button>
            </Card>
          </div>}

        {/* SETTINGS TAB */}
        {currentTab === 'settings' && <div className="animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-3918">
            <div className="flex gap-6" data-id="element-3919">
              {/* Sidebar */}
              <div className="w-64 flex-shrink-0" data-id="element-3920">
                <Card noPadding data-id="element-3921">
                  <div className="p-4 border-b border-slate-200" data-id="element-3922">
                    <h3 className="font-semibold text-slate-900" data-id="element-3923">
                      Community Settings
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5" data-id="element-3924">
                      View & update data
                    </p>
                  </div>
                  <nav className="p-2 space-y-1" data-id="element-3925">
                    {[{
                  id: 'info',
                  label: 'Community Info',
                  icon: Building2
                }, {
                  id: 'media',
                  label: 'Media',
                  icon: Image
                }, {
                  id: 'documents',
                  label: 'Documents',
                  icon: FileText
                }, ...(isAdmin ? [{
                  id: 'builds',
                  label: 'Builds',
                  icon: Package
                }, {
                  id: 'listings',
                  label: 'Listings',
                  icon: Monitor
                }] : []), {
                  id: 'buyer-experience',
                  label: 'Buyer Experience',
                  icon: Palette
                }, ...(isAdmin ? [{
                  id: 'gdn-launcher',
                  label: 'GDN Launcher Settings',
                  icon: Rocket
                }, {
                  id: 'gdn-launcher-toggle',
                  label: 'GDN: Toggle View',
                  icon: Rocket
                }, {
                  id: 'gdn-launcher-drawer',
                  label: 'GDN: Drawer View',
                  icon: Rocket
                }] : [])].map(section => {
                  const Icon = section.icon;
                  return <button key={section.id} onClick={() => navigate(`${basePath}/settings/${section.id}`)} className={`
                            w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors
                            ${settingsSection === section.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}
                          `} data-id="element-3926">
                          <Icon className="h-4 w-4" data-id="element-3927" />
                          {section.label}
                        </button>;
                })}
                  </nav>
                </Card>
              </div>

              {/* Main Content */}
              <div className="flex-1 space-y-6" data-id="element-3928">
                {/* Save Bar */}
                {hasChanges && <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between" data-id="element-3929">
                    <p className="text-sm text-amber-800 font-medium" data-id="element-3930">
                      You have unsaved changes
                    </p>
                    <Button onClick={() => setHasChanges(false)} leftIcon={<Save className="h-4 w-4" data-id="element-3932" />} data-id="element-3931">
                      Save Changes
                    </Button>
                  </div>}

                {/* Section Content */}
                {settingsSection === 'info' && <div className="space-y-6" data-id="element-3933">
                    {isAdmin ? <>
                        {/* Admin: Stats Cards */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-3934">
                          {[{
                    label: 'Active Buyers',
                    value: `${community.buyerCount}`,
                    icon: Users,
                    color: 'text-primary-600 bg-primary-50'
                  }, {
                    label: 'Models',
                    value: `${community.modelCount}`,
                    icon: Building2,
                    color: 'text-slate-600 bg-slate-100'
                  }, {
                    label: 'Active Options',
                    value: '48',
                    icon: Settings,
                    color: 'text-slate-600 bg-slate-100'
                  }, {
                    label: 'Avg Session',
                    value: '4m 32s',
                    icon: Activity,
                    color: 'text-slate-600 bg-slate-100'
                  }].map(stat => {
                    const StatIcon = stat.icon;
                    return <Card key={stat.label} className="p-4 flex flex-col items-center text-center gap-2" data-id="element-3935">
                                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${stat.color}`} data-id="element-3936">
                                  <StatIcon className="h-5 w-5" data-id="element-3937" />
                                </div>
                                <p className="text-xs font-medium text-slate-500" data-id="element-3938">
                                  {stat.label}
                                </p>
                                <p className="text-lg font-bold text-slate-900" data-id="element-3939">
                                  {stat.value}
                                </p>
                              </Card>;
                  })}
                        </div>

                        {/* Admin: Basic Information */}
                        <Card className="p-8 space-y-8" data-id="element-3940">
                          <div className="border-b border-slate-100 pb-6" data-id="element-3941">
                            <h3 className="text-lg font-semibold text-slate-900" data-id="element-3942">
                              Basic Information
                            </h3>
                            <p className="text-sm text-slate-500 mt-1" data-id="element-3943">
                              General details about this community.
                            </p>
                          </div>

                          <div className="space-y-6" data-id="element-3944">
                            <div data-id="element-3945">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3946">
                                Community Name
                              </label>
                              <input type="text" defaultValue={community.name} onChange={() => setHasChanges(true)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900" data-id="element-3947" />
                            </div>

                            <div data-id="element-3948">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3949">
                                Status
                              </label>
                              <div className="relative" data-id="element-3950">
                                <select defaultValue={community.status} onChange={() => setHasChanges(true)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none bg-white appearance-none pr-10 transition-shadow text-slate-900" data-id="element-3951">
                                  <option value="active" data-id="element-3952">Active</option>
                                  <option value="draft" data-id="element-3953">Draft</option>
                                  <option value="archived" data-id="element-3954">Archived</option>
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" data-id="element-3955" />
                              </div>
                            </div>

                            <div data-id="element-3956">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3957">
                                Description
                              </label>
                              <textarea defaultValue={community.description} onChange={() => setHasChanges(true)} rows={4} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none resize-none transition-shadow text-slate-900" data-id="element-3958" />
                            </div>

                            <div data-id="element-3959">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3960">
                                GDN CMS ID
                              </label>
                              <input type="text" defaultValue="963258741" onChange={() => setHasChanges(true)} placeholder="Example: 963258741" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-3961" />
                            </div>
                          </div>
                        </Card>

                        {/* Admin: Branding & Images */}
                        <Card className="p-8 space-y-8" data-id="element-3962">
                          <div className="border-b border-slate-100 pb-6" data-id="element-3963">
                            <h3 className="text-lg font-semibold text-slate-900" data-id="element-3964">
                              Branding & Images
                            </h3>
                            <p className="text-sm text-slate-500 mt-1" data-id="element-3965">
                              Logos and visual assets for this community.
                            </p>
                          </div>

                          <div className="space-y-6" data-id="element-3966">
                            {[{
                      label: 'Builder Logo',
                      desc: 'Primary logo displayed in the buyer experience'
                    }, {
                      label: 'Builder Site Logo',
                      desc: 'Secondary logo for the community site header'
                    }, {
                      label: 'Background Image',
                      desc: 'Hero background shown on the community landing'
                    }].map(field => <div key={field.label} data-id="element-3967">
                                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-3968">
                                  {field.label}
                                </label>
                                <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer group" onDragOver={e => {
                        e.preventDefault();
                        e.currentTarget.classList.add('border-primary-400', 'bg-primary-50/50');
                      }} onDragLeave={e => {
                        e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50/50');
                      }} onDrop={e => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50/50');
                        setHasChanges(true);
                      }} data-id="element-3969">
                                  <div className="flex flex-col items-center gap-3" data-id="element-3970">
                                    <div className="h-12 w-12 rounded-xl bg-slate-100 group-hover:bg-primary-100 flex items-center justify-center transition-colors" data-id="element-3971">
                                      <Upload className="h-5 w-5 text-slate-400 group-hover:text-primary-500 transition-colors" data-id="element-3972" />
                                    </div>
                                    <div data-id="element-3973">
                                      <p className="text-sm font-medium text-slate-700 group-hover:text-primary-700 transition-colors" data-id="element-3974">
                                        Drop an image here, or{' '}
                                        <span className="text-primary-600" data-id="element-3975">
                                          browse
                                        </span>
                                      </p>
                                      <p className="text-xs text-slate-400 mt-1" data-id="element-3976">
                                        {field.desc}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>)}

                            <div className="flex items-start gap-3 p-4 bg-blue-50/70 border border-blue-100 rounded-xl" data-id="element-3977">
                              <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-3978">
                                <Info className="h-4 w-4 text-blue-600" data-id="element-3979" />
                              </div>
                              <div data-id="element-3980">
                                <p className="text-sm font-medium text-blue-900" data-id="element-3981">
                                  Media files required
                                </p>
                                <p className="text-sm text-blue-700 mt-0.5" data-id="element-3982">
                                  Upload logo and background images in the{' '}
                                  <button onClick={() => navigate(`${basePath}/settings/media`)} className="font-semibold underline underline-offset-2 hover:text-blue-900 transition-colors" data-id="element-3983">
                                    Media
                                  </button>{' '}
                                  tab first, then select them here.
                                </p>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </> : <>
                        <div data-id="element-3984">
                          <h1 className="text-2xl font-bold text-slate-900" data-id="element-3985">
                            Community Info
                          </h1>
                          <p className="text-slate-500" data-id="element-3986">
                            General information and configuration.
                          </p>
                        </div>
                        <Card className="p-6 space-y-6" data-id="element-3987">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3988">
                            <div data-id="element-3989">
                              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3990">
                                Community Name
                              </label>
                              <input type="text" defaultValue={community.name} onChange={() => setHasChanges(true)} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-3991" />
                            </div>
                            <div data-id="element-3992">
                              <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3993">
                                Region
                              </label>
                              <select defaultValue={community.regionId} onChange={() => setHasChanges(true)} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white" data-id="element-3994">
                                <option value="alberta" data-id="element-3995">Alberta Region</option>
                                <option value="ontario" data-id="element-3996">Ontario Region</option>
                                <option value="bc" data-id="element-3997">BC Region</option>
                              </select>
                            </div>
                          </div>
                          <div data-id="element-3998">
                            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3999">
                              Description
                            </label>
                            <textarea defaultValue={community.description} onChange={() => setHasChanges(true)} rows={4} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none" data-id="element-4000" />
                          </div>
                          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200" data-id="element-4001">
                            <div data-id="element-4002">
                              <p className="font-medium text-slate-900" data-id="element-4003">
                                Community Status
                              </p>
                              <p className="text-sm text-slate-500" data-id="element-4004">
                                Active communities are visible to buyers.
                              </p>
                            </div>
                            <Toggle checked={community.status === 'active'} onChange={() => setHasChanges(true)} data-id="element-4005" />
                          </div>
                        </Card>
                      </>}
                  </div>}

                {settingsSection === 'media' && <div className="space-y-6" data-id="element-4006">
                    {/* Hero Image */}
                    <Card className="p-8 space-y-6" data-id="element-4007">
                      <div className="border-b border-slate-100 pb-6" data-id="element-4008">
                        <h3 className="text-lg font-semibold text-slate-900" data-id="element-4009">
                          Hero Image
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-4010">
                          The main image displayed at the top of the community
                          page.
                        </p>
                      </div>

                      {/* Preview */}
                      {community.image && <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-100" data-id="element-4011">
                          <img src={community.image} alt={`${community.name} hero`} className="w-full h-48 object-cover" data-id="element-4012" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" data-id="element-4013" />
                          <button className="absolute top-3 right-3 p-1.5 bg-white/90 backdrop-blur-sm rounded-lg text-slate-600 hover:text-red-500 hover:bg-white transition-all shadow-sm" data-id="element-4014">
                            <Trash2 className="h-4 w-4" data-id="element-4015" />
                          </button>
                        </div>}

                      {/* Upload zone */}
                      <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer group" onDragOver={e => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-primary-400', 'bg-primary-50/50');
                }} onDragLeave={e => {
                  e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50/50');
                }} onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50/50');
                  setHasChanges(true);
                }} data-id="element-4016">
                        <div className="flex flex-col items-center gap-3" data-id="element-4017">
                          <div className="h-12 w-12 rounded-xl bg-slate-100 group-hover:bg-primary-100 flex items-center justify-center transition-colors" data-id="element-4018">
                            <Upload className="h-5 w-5 text-slate-400 group-hover:text-primary-500 transition-colors" data-id="element-4019" />
                          </div>
                          <div data-id="element-4020">
                            <p className="text-sm font-medium text-slate-700 group-hover:text-primary-700 transition-colors" data-id="element-4021">
                              Drop an image here, or{' '}
                              <span className="text-primary-600" data-id="element-4022">browse</span>
                            </p>
                            <p className="text-xs text-slate-400 mt-1" data-id="element-4023">
                              PNG, JPG, or WebP — recommended 1920×600
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Divider with "or" */}
                      <div className="flex items-center gap-4" data-id="element-4024">
                        <div className="flex-1 h-px bg-slate-200" data-id="element-4025" />
                        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-4026">
                          or paste a URL
                        </span>
                        <div className="flex-1 h-px bg-slate-200" data-id="element-4027" />
                      </div>

                      {/* URL input */}
                      <div data-id="element-4028">
                        <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4029">
                          Image URL
                        </label>
                        <input type="text" defaultValue={community.image} onChange={() => setHasChanges(true)} placeholder="https://example.com/hero-image.jpg" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4030" />
                        <p className="text-xs text-slate-400 mt-1.5" data-id="element-4031">
                          Enter a direct link to an image hosted externally.
                        </p>
                      </div>
                    </Card>

                    {/* Gallery Images */}
                    <Card className="p-8 space-y-6" data-id="element-4032">
                      <div className="border-b border-slate-100 pb-6" data-id="element-4033">
                        <h3 className="text-lg font-semibold text-slate-900" data-id="element-4034">
                          Gallery Images
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-4035">
                          Additional images shown in the community gallery and
                          model pages.
                        </p>
                      </div>

                      <div className="border-2 border-dashed border-slate-200 rounded-xl p-10 text-center hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer group" onDragOver={e => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-primary-400', 'bg-primary-50/50');
                }} onDragLeave={e => {
                  e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50/50');
                }} onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50/50');
                  setHasChanges(true);
                }} data-id="element-4036">
                        <div className="flex flex-col items-center gap-3" data-id="element-4037">
                          <div className="h-12 w-12 rounded-xl bg-slate-100 group-hover:bg-primary-100 flex items-center justify-center transition-colors" data-id="element-4038">
                            <Image className="h-5 w-5 text-slate-400 group-hover:text-primary-500 transition-colors" data-id="element-4039" />
                          </div>
                          <div data-id="element-4040">
                            <p className="text-sm font-medium text-slate-700 group-hover:text-primary-700 transition-colors" data-id="element-4041">
                              Drop images here, or{' '}
                              <span className="text-primary-600" data-id="element-4042">browse</span>
                            </p>
                            <p className="text-xs text-slate-400 mt-1" data-id="element-4043">
                              PNG, JPG, or WebP — upload multiple files at once
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>}

                {settingsSection === 'documents' && <div className="space-y-6" data-id="element-4044">
                    <div data-id="element-4045">
                      <h1 className="text-2xl font-bold text-slate-900" data-id="element-4046">
                        Documents
                      </h1>
                      <p className="text-slate-500" data-id="element-4047">
                        Manage community files and resources.
                      </p>
                    </div>
                    <Card className="p-6" data-id="element-4048">
                      <div className="text-center py-12" data-id="element-4049">
                        <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" data-id="element-4050" />
                        <h3 className="text-lg font-medium text-slate-900" data-id="element-4051">
                          No documents uploaded
                        </h3>
                        <p className="text-slate-500 mt-1 mb-6" data-id="element-4052">
                          Upload site plans, brochures, and other resources.
                        </p>
                        <Button variant="outline" data-id="element-4053">Upload Document</Button>
                      </div>
                    </Card>
                  </div>}

                {settingsSection === 'builds' && isAdmin && <div className="space-y-6" data-id="element-4054">
                    <Card className="p-8" data-id="element-4055">
                      <div className="flex items-start justify-between pb-6 border-b border-slate-100" data-id="element-4056">
                        <div data-id="element-4057">
                          <h3 className="text-lg font-semibold text-slate-900" data-id="element-4058">
                            Community Builds
                          </h3>
                          <p className="text-sm text-slate-500 mt-1" data-id="element-4059">
                            Upload and manage build files for this community.
                          </p>
                        </div>
                        <Button leftIcon={<Upload className="h-4 w-4" data-id="element-4061" />} size="sm" data-id="element-4060">
                          Upload build
                        </Button>
                      </div>

                      {/* Drop zone */}
                      <div className="mt-6 border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-primary-300 hover:bg-primary-50/30 transition-all cursor-pointer group" onDragOver={e => {
                  e.preventDefault();
                  e.currentTarget.classList.add('border-primary-400', 'bg-primary-50/50');
                }} onDragLeave={e => {
                  e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50/50');
                }} onDrop={e => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-primary-400', 'bg-primary-50/50');
                }} data-id="element-4062">
                        <div className="flex flex-col items-center gap-2" data-id="element-4063">
                          <div className="h-10 w-10 rounded-xl bg-slate-100 group-hover:bg-primary-100 flex items-center justify-center transition-colors" data-id="element-4064">
                            <Upload className="h-5 w-5 text-slate-400 group-hover:text-primary-500 transition-colors" data-id="element-4065" />
                          </div>
                          <p className="text-sm text-slate-500 group-hover:text-primary-700 transition-colors" data-id="element-4066">
                            Drop a build file here, or{' '}
                            <span className="text-primary-600 font-medium" data-id="element-4067">
                              browse
                            </span>
                          </p>
                          <p className="text-xs text-slate-400" data-id="element-4068">
                            .pak, .zip, or .tar.gz — max 10 GB
                          </p>
                        </div>
                      </div>

                      {/* Builds Table */}
                      <div className="mt-6" data-id="element-4069">
                        <div className="overflow-hidden rounded-lg border border-slate-200" data-id="element-4070">
                          <table className="w-full text-sm" data-id="element-4071">
                            <thead data-id="element-4072">
                              <tr className="bg-slate-50 border-b border-slate-200" data-id="element-4073">
                                <th className="text-left px-4 py-3 font-medium text-slate-500" data-id="element-4074">
                                  File Name
                                </th>
                                <th className="text-left px-4 py-3 font-medium text-slate-500" data-id="element-4075">
                                  Version
                                </th>
                                <th className="text-left px-4 py-3 font-medium text-slate-500" data-id="element-4076">
                                  Size
                                </th>
                                <th className="text-left px-4 py-3 font-medium text-slate-500" data-id="element-4077">
                                  Uploaded
                                </th>
                                <th className="text-left px-4 py-3 font-medium text-slate-500" data-id="element-4078">
                                  Status
                                </th>
                                <th className="text-right px-4 py-3 font-medium text-slate-500" data-id="element-4079"></th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100" data-id="element-4080">
                              {[{
                          name: 'oakwood-estates-v3.2.pak',
                          version: 'v3.2',
                          size: '2.4 GB',
                          date: 'Mar 18, 2026',
                          status: 'active'
                        }, {
                          name: 'oakwood-estates-v3.1.pak',
                          version: 'v3.1',
                          size: '2.3 GB',
                          date: 'Mar 10, 2026',
                          status: 'archived'
                        }, {
                          name: 'oakwood-estates-v3.0.pak',
                          version: 'v3.0',
                          size: '2.1 GB',
                          date: 'Feb 28, 2026',
                          status: 'archived'
                        }].map((build, i) => <tr key={i} className="hover:bg-slate-50/50 transition-colors" data-id="element-4081">
                                  <td className="px-4 py-3.5" data-id="element-4082">
                                    <div className="flex items-center gap-2.5" data-id="element-4083">
                                      <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0" data-id="element-4084">
                                        <Package className="h-4 w-4 text-slate-400" data-id="element-4085" />
                                      </div>
                                      <span className="font-medium text-slate-900" data-id="element-4086">
                                        {build.name}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3.5" data-id="element-4087">
                                    <span className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-medium" data-id="element-4088">
                                      {build.version}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3.5 text-slate-600" data-id="element-4089">
                                    {build.size}
                                  </td>
                                  <td className="px-4 py-3.5 text-slate-600" data-id="element-4090">
                                    {build.date}
                                  </td>
                                  <td className="px-4 py-3.5" data-id="element-4091">
                                    {build.status === 'active' ? <Badge variant="success" data-id="element-4092">Active</Badge> : <Badge variant="neutral" data-id="element-4093">Archived</Badge>}
                                  </td>
                                  <td className="px-4 py-3.5 text-right" data-id="element-4094">
                                    <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors" data-id="element-4095">
                                      <MoreVertical className="h-4 w-4" data-id="element-4096" />
                                    </button>
                                  </td>
                                </tr>)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </Card>
                  </div>}

                {settingsSection === 'listings' && isAdmin && <div className="space-y-6" data-id="element-4097">
                    <Card className="p-8 space-y-6" data-id="element-4098">
                      <div className="border-b border-slate-100 pb-6" data-id="element-4099">
                        <h3 className="text-lg font-semibold text-slate-900" data-id="element-4100">
                          Listing Settings
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-4101">
                          Configure how this community appears in public
                          listings.
                        </p>
                      </div>
                      <div data-id="element-4102">
                        <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4103">
                          Listing Title
                        </label>
                        <input type="text" defaultValue="" onChange={() => setHasChanges(true)} placeholder="Display title for listings" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4104" />
                        <p className="text-xs text-slate-400 mt-1.5" data-id="element-4105">
                          The public-facing name shown in community listings and
                          search results.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4106">
                        <div data-id="element-4107">
                          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4108">
                            Surface Unit
                          </label>
                          <select defaultValue="" onChange={() => setHasChanges(true)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 bg-white" data-id="element-4109">
                            <option value="" disabled data-id="element-4110">
                              Select unit
                            </option>
                            <option value="sqft" data-id="element-4111">sq.ft (Square Feet)</option>
                            <option value="sqm" data-id="element-4112">m² (Square Meters)</option>
                          </select>
                          <p className="text-xs text-slate-400 mt-1.5" data-id="element-4113">
                            Unit of measurement for floor areas.
                          </p>
                        </div>
                        <div data-id="element-4114">
                          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4115">
                            Price Currency
                          </label>
                          <select defaultValue="" onChange={() => setHasChanges(true)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 bg-white" data-id="element-4116">
                            <option value="" disabled data-id="element-4117">
                              Select currency
                            </option>
                            <option value="USD" data-id="element-4118">USD ($)</option>
                            <option value="CAD" data-id="element-4119">CAD (C$)</option>
                            <option value="EUR" data-id="element-4120">EUR (€)</option>
                            <option value="GBP" data-id="element-4121">GBP (£)</option>
                            <option value="AUD" data-id="element-4122">AUD (A$)</option>
                          </select>
                          <p className="text-xs text-slate-400 mt-1.5" data-id="element-4123">
                            Currency symbol or code displayed with prices.
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>}

                {settingsSection === 'buyer-experience' && <div className="space-y-6" data-id="element-4124">
                    {isAdmin ? <>
                        {/* User-Login Preferences */}
                        <Card className="p-8 space-y-6" data-id="element-4125">
                          <div className="border-b border-slate-100 pb-6" data-id="element-4126">
                            <h3 className="text-lg font-semibold text-slate-900" data-id="element-4127">
                              User-Login Preferences
                            </h3>
                            <p className="text-sm text-slate-500 mt-1" data-id="element-4128">
                              Control how clients access the buyer experience.
                            </p>
                          </div>
                          <div className="flex items-center justify-between gap-8 py-1" data-id="element-4129">
                            <div className="flex-1" data-id="element-4130">
                              <p className="text-sm font-medium text-slate-900" data-id="element-4131">
                                Enable Client Login
                              </p>
                              <p className="text-sm text-slate-500 mt-0.5" data-id="element-4132">
                                Allow clients to log in using a 4-digit user ID.
                              </p>
                            </div>
                            <Toggle checked={clientLoginEnabled} onChange={() => {
                      setClientLoginEnabled(!clientLoginEnabled);
                      setHasChanges(true);
                    }} data-id="element-4133" />
                          </div>

                          {clientLoginEnabled && <div className="space-y-5 pt-4 pl-0 border-t border-slate-100 mt-4" data-id="element-4134">
                              <div data-id="element-4135">
                                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4136">
                                  Login Box Text
                                </label>
                                <input type="text" defaultValue={`Have you designed your dream home with ${community.name}?`} onChange={() => setHasChanges(true)} placeholder="Enter the message shown on the login box" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4137" />
                                <p className="text-xs text-slate-400 mt-1.5" data-id="element-4138">
                                  Displayed above the login form to prompt
                                  buyers.
                                </p>
                              </div>
                              <div data-id="element-4139">
                                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4140">
                                  Registration Page URL
                                </label>
                                <input type="url" defaultValue="" onChange={() => setHasChanges(true)} placeholder="https://example.com/register" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4141" />
                                <p className="text-xs text-slate-400 mt-1.5" data-id="element-4142">
                                  Link to the registration page for new buyers.
                                </p>
                              </div>
                            </div>}
                        </Card>

                        {/* Tour Flow Preferences */}
                        <Card className="p-8 space-y-6" data-id="element-4143">
                          <div className="border-b border-slate-100 pb-6" data-id="element-4144">
                            <h3 className="text-lg font-semibold text-slate-900" data-id="element-4145">
                              Tour Flow Preferences
                            </h3>
                            <p className="text-sm text-slate-500 mt-1" data-id="element-4146">
                              Configure how buyers navigate through the virtual
                              experience.
                            </p>
                          </div>
                          <div className="space-y-1" data-id="element-4147">
                            <div className="flex items-center justify-between gap-8 py-4 border-b border-slate-50" data-id="element-4148">
                              <div className="flex-1" data-id="element-4149">
                                <p className="text-sm font-medium text-slate-900" data-id="element-4150">
                                  Guided Tour Mode
                                </p>
                                <p className="text-sm text-slate-500 mt-0.5" data-id="element-4151">
                                  Walk buyers through a structured, step-by-step
                                  experience.
                                </p>
                              </div>
                              <Toggle checked={false} onChange={() => setHasChanges(true)} data-id="element-4152" />
                            </div>
                            <div className="flex items-center justify-between gap-8 py-4" data-id="element-4153">
                              <div className="flex-1" data-id="element-4154">
                                <p className="text-sm font-medium text-slate-900" data-id="element-4155">
                                  Show Progress Indicator
                                </p>
                                <p className="text-sm text-slate-500 mt-0.5" data-id="element-4156">
                                  Display a completion percentage as buyers move
                                  through the tour.
                                </p>
                              </div>
                              <Toggle checked={false} onChange={() => setHasChanges(true)} data-id="element-4157" />
                            </div>
                          </div>
                        </Card>

                        {/* Graphic Optimisation */}
                        <Card className="p-8 space-y-6" data-id="element-4158">
                          <div className="border-b border-slate-100 pb-6" data-id="element-4159">
                            <h3 className="text-lg font-semibold text-slate-900" data-id="element-4160">
                              Graphic Optimisation
                            </h3>
                            <p className="text-sm text-slate-500 mt-1" data-id="element-4161">
                              Fine-tune rendering performance and visual quality
                              settings.
                            </p>
                          </div>
                          <div className="space-y-6" data-id="element-4162">
                            <div className="flex items-center justify-between gap-8 py-1" data-id="element-4163">
                              <div className="flex-1" data-id="element-4164">
                                <p className="text-sm font-medium text-slate-900" data-id="element-4165">
                                  Show Settings Button
                                </p>
                                <p className="text-sm text-slate-500 mt-0.5" data-id="element-4166">
                                  Display a graphics settings button in the
                                  buyer experience.
                                </p>
                              </div>
                              <Toggle checked={false} onChange={() => setHasChanges(true)} data-id="element-4167" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2" data-id="element-4168">
                              <div data-id="element-4169">
                                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4170">
                                  Screen Percentage
                                </label>
                                <div className="relative" data-id="element-4171">
                                  <input type="number" defaultValue={100} onChange={() => setHasChanges(true)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900" data-id="element-4172" />
                                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-slate-400 pointer-events-none" data-id="element-4173">
                                    %
                                  </span>
                                </div>
                                <p className="text-xs text-slate-400 mt-1.5" data-id="element-4174">
                                  Resolution scale relative to display. Default:
                                  100
                                </p>
                              </div>
                              <div data-id="element-4175">
                                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4176">
                                  Overall Scalability
                                </label>
                                <input type="number" defaultValue={5} onChange={() => setHasChanges(true)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900" data-id="element-4177" />
                                <p className="text-xs text-slate-400 mt-1.5" data-id="element-4178">
                                  Quality level from 1 (low) to 10 (ultra).
                                  Default: 5
                                </p>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </> : <>
                        <div data-id="element-4179">
                          <h1 className="text-2xl font-bold text-slate-900" data-id="element-4180">
                            Buyer Experience
                          </h1>
                          <p className="text-slate-500" data-id="element-4181">
                            Configure what buyers see for this community.
                          </p>
                        </div>
                        <Card className="p-6 space-y-6" data-id="element-4182">
                          <div className="flex items-center justify-between" data-id="element-4183">
                            <div data-id="element-4184">
                              <p className="font-medium text-slate-900" data-id="element-4185">
                                Enable Virtual Tours
                              </p>
                              <p className="text-sm text-slate-500" data-id="element-4186">
                                Allow buyers to explore models in 3D.
                              </p>
                            </div>
                            <Toggle checked={true} onChange={() => setHasChanges(true)} data-id="element-4187" />
                          </div>
                          <div className="h-px bg-slate-100" data-id="element-4188" />
                          <div className="flex items-center justify-between" data-id="element-4189">
                            <div data-id="element-4190">
                              <p className="font-medium text-slate-900" data-id="element-4191">
                                Show Pricing to Buyers
                              </p>
                              <p className="text-sm text-slate-500" data-id="element-4192">
                                Display base prices and option costs.
                              </p>
                            </div>
                            <Toggle checked={true} onChange={() => setHasChanges(true)} data-id="element-4193" />
                          </div>
                          <div className="h-px bg-slate-100" data-id="element-4194" />
                          <div className="flex items-center justify-between" data-id="element-4195">
                            <div data-id="element-4196">
                              <p className="font-medium text-slate-900" data-id="element-4197">
                                Allow Option Comparisons
                              </p>
                              <p className="text-sm text-slate-500" data-id="element-4198">
                                Let buyers compare different option levels
                                side-by-side.
                              </p>
                            </div>
                            <Toggle checked={false} onChange={() => setHasChanges(true)} data-id="element-4199" />
                          </div>
                        </Card>
                      </>}
                  </div>}

                {settingsSection === 'gdn-launcher' && isAdmin && <div className="space-y-6" data-id="element-4200">
                    {/* GDN Launcher Fields */}
                    <Card className="p-8 space-y-8" data-id="element-4201">
                      <div className="border-b border-slate-100 pb-6" data-id="element-4202">
                        <h3 className="text-lg font-semibold text-slate-900" data-id="element-4203">
                          GDN Launcher
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-4204">
                          Customize GDN Launcher appearance
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4205">
                        <div data-id="element-4206">
                          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4207">
                            Title
                          </label>
                          <input type="text" value={gdnTitle} onChange={e => {
                      setGdnTitle(e.target.value);
                      setHasChanges(true);
                    }} placeholder="Launcher title" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4208" />
                        </div>
                        <div data-id="element-4209">
                          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4210">
                            Tour Redirect Type
                          </label>
                          <select defaultValue="magic-link" onChange={() => setHasChanges(true)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 bg-white" data-id="element-4211">
                            <option value="magic-link" data-id="element-4212">Magic Link</option>
                            <option value="direct" data-id="element-4213">Direct</option>
                            <option value="qr-code" data-id="element-4214">QR Code</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4215">
                        <div data-id="element-4216">
                          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4217">
                            Homepage URL (Exit Redirection)
                          </label>
                          <input type="url" defaultValue="" onChange={() => setHasChanges(true)} placeholder="https://example.com" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4218" />
                        </div>
                        <div data-id="element-4219">
                          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4220">
                            Inactivity Timeout (s)
                          </label>
                          <input type="number" defaultValue={180} onChange={() => setHasChanges(true)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900" data-id="element-4221" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4222">
                        <div data-id="element-4223">
                          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4224">
                            Button Text
                          </label>
                          <input type="text" value={gdnButtonText} onChange={e => {
                      setGdnButtonText(e.target.value);
                      setHasChanges(true);
                    }} placeholder="Enter button text" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4225" />
                        </div>
                        <div data-id="element-4226">
                          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4227">
                            Exit Text
                          </label>
                          <input type="text" value={gdnExitText} onChange={e => {
                      setGdnExitText(e.target.value);
                      setHasChanges(true);
                    }} placeholder="Enter exit text" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4228" />
                        </div>
                      </div>
                    </Card>

                    {/* Live Preview */}
                    <Card className="p-0 overflow-hidden" data-id="element-4229">
                      <div className="relative w-full aspect-[16/9] bg-slate-900 flex items-center justify-center" data-id="element-4230">
                        <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200" alt="Background" className="absolute inset-0 w-full h-full object-cover blur-sm scale-105" data-id="element-4231" />
                        <div className="absolute inset-0" style={{
                    backgroundColor: gdnOverlayColor,
                    opacity: 0.7
                  }} data-id="element-4232" />
                        <div className="absolute top-4 left-4 z-20 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10" data-id="element-4233">
                          <span className="text-xs font-semibold text-white/80 uppercase tracking-wider" data-id="element-4234">
                            Live Preview
                          </span>
                        </div>
                        <div className="absolute top-6 right-6 z-10" data-id="element-4235">
                          <div className="p-2 rounded-full border border-white/20 bg-black/20 backdrop-blur-sm" data-id="element-4236">
                            <Settings className="h-5 w-5 text-white/60" data-id="element-4237" />
                          </div>
                        </div>
                        <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-2xl px-8" data-id="element-4238">
                          <div className="h-24 w-24 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mb-10 shadow-2xl" data-id="element-4239">
                            <HomeIcon className="h-10 w-10 text-white/80" data-id="element-4240" />
                          </div>
                          <h1 className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-[0.2em] text-center mb-10 drop-shadow-lg" data-id="element-4241">
                            {gdnTitle || 'VIRTUAL EXPERIENCE'}
                          </h1>
                          <button className="flex items-center gap-3 px-8 py-4 rounded-lg font-semibold uppercase tracking-wider transition-transform hover:scale-105 shadow-xl" style={{
                      backgroundColor: gdnPrimaryColor,
                      color: gdnOnPrimaryColor
                    }} data-id="element-4242">
                            {gdnButtonText || 'BEGIN TOUR'}
                            <ArrowRight className="h-5 w-5" data-id="element-4243" />
                          </button>
                          <button className="mt-8 flex items-center gap-2 text-white/60 hover:text-white transition-colors" data-id="element-4244">
                            <LogOut className="h-4 w-4" data-id="element-4245" />
                            <span className="text-sm font-medium" data-id="element-4246">
                              {gdnExitText || 'Exit'}
                            </span>
                          </button>
                        </div>
                      </div>
                    </Card>

                    {/* Theme Settings */}
                    <Card className="p-8 space-y-8" data-id="element-4247">
                      <div className="border-b border-slate-100 pb-6" data-id="element-4248">
                        <h3 className="text-lg font-semibold text-slate-900" data-id="element-4249">
                          Theme Settings
                        </h3>
                        <p className="text-sm text-slate-500 mt-1" data-id="element-4250">
                          Customize the visual appearance of the GDN Launcher.
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4251">
                        <div data-id="element-4252">
                          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4253">
                            Primary Color
                          </label>
                          <div className="flex items-center gap-3" data-id="element-4254">
                            <input type="color" value={gdnPrimaryColor} onChange={e => {
                        setGdnPrimaryColor(e.target.value);
                        setHasChanges(true);
                      }} className="h-10 w-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white" data-id="element-4255" />
                            <input type="text" value={gdnPrimaryColor} onChange={e => {
                        setGdnPrimaryColor(e.target.value);
                        setHasChanges(true);
                      }} placeholder="#1e293b" className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400 font-mono text-sm" data-id="element-4256" />
                          </div>
                        </div>
                        <div data-id="element-4257">
                          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4258">
                            Overlay Color
                          </label>
                          <div className="flex items-center gap-3" data-id="element-4259">
                            <input type="color" value={gdnOverlayColor} onChange={e => {
                        setGdnOverlayColor(e.target.value);
                        setHasChanges(true);
                      }} className="h-10 w-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white" data-id="element-4260" />
                            <input type="text" value={gdnOverlayColor} onChange={e => {
                        setGdnOverlayColor(e.target.value);
                        setHasChanges(true);
                      }} placeholder="#0f172a" className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400 font-mono text-sm" data-id="element-4261" />
                          </div>
                        </div>
                        <div data-id="element-4262">
                          <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4263">
                            On Primary Color
                          </label>
                          <div className="flex items-center gap-3" data-id="element-4264">
                            <input type="color" value={gdnOnPrimaryColor} onChange={e => {
                        setGdnOnPrimaryColor(e.target.value);
                        setHasChanges(true);
                      }} className="h-10 w-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white" data-id="element-4265" />
                            <input type="text" value={gdnOnPrimaryColor} onChange={e => {
                        setGdnOnPrimaryColor(e.target.value);
                        setHasChanges(true);
                      }} placeholder="#ffffff" className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400 font-mono text-sm" data-id="element-4266" />
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>}

                {settingsSection === 'gdn-launcher-toggle' && isAdmin && <div className="space-y-6 relative h-[calc(100vh-16rem)] flex flex-col" data-id="element-4267">
                    {/* Top Toggle Bar */}
                    <div className="flex items-center justify-center p-3 border-b border-slate-200 bg-slate-50 z-30 flex-shrink-0 rounded-t-xl border-x border-t" data-id="element-4268">
                      <div className="bg-slate-200/50 p-1 rounded-lg flex items-center gap-1" data-id="element-4269">
                        <button onClick={() => setGdnViewMode('preview')} className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${gdnViewMode === 'preview' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4270">
                          <Eye className="h-4 w-4" data-id="element-4271" />
                          Live Preview
                        </button>
                        <button onClick={() => setGdnViewMode('editor')} className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-all ${gdnViewMode === 'editor' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4272">
                          <LayoutTemplate className="h-4 w-4" data-id="element-4273" />
                          Editor
                        </button>
                      </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1 relative overflow-hidden rounded-b-xl border-x border-b border-slate-200" data-id="element-4274">
                      {/* PREVIEW MODE */}
                      <div className={`absolute inset-0 transition-opacity duration-300 ${gdnViewMode === 'preview' ? 'opacity-100 z-20' : 'opacity-0 z-0 pointer-events-none'}`} data-id="element-4275">
                        <div className="h-full w-full relative bg-slate-900 overflow-hidden flex items-center justify-center" data-id="element-4276">
                          <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200" alt="Background" className="absolute inset-0 w-full h-full object-cover blur-sm scale-105" data-id="element-4277" />
                          <div className="absolute inset-0" style={{
                      backgroundColor: gdnOverlayColor,
                      opacity: 0.7
                    }} data-id="element-4278" />
                          <div className="absolute top-4 left-4 z-20 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10" data-id="element-4279">
                            <span className="text-xs font-semibold text-white/80 uppercase tracking-wider" data-id="element-4280">
                              Live Preview
                            </span>
                          </div>
                          <div className="absolute top-6 right-6 z-10" data-id="element-4281">
                            <div className="p-2 rounded-full border border-white/20 bg-black/20 backdrop-blur-sm" data-id="element-4282">
                              <Settings className="h-5 w-5 text-white/60" data-id="element-4283" />
                            </div>
                          </div>
                          <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-2xl px-8" data-id="element-4284">
                            <div className="h-24 w-24 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mb-10 shadow-2xl" data-id="element-4285">
                              <HomeIcon className="h-10 w-10 text-white/80" data-id="element-4286" />
                            </div>
                            <h1 className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-[0.2em] text-center mb-10 drop-shadow-lg" data-id="element-4287">
                              {gdnTitle || 'VIRTUAL EXPERIENCE'}
                            </h1>
                            <button className="flex items-center gap-3 px-8 py-4 rounded-lg font-semibold uppercase tracking-wider transition-transform hover:scale-105 shadow-xl" style={{
                        backgroundColor: gdnPrimaryColor,
                        color: gdnOnPrimaryColor
                      }} data-id="element-4288">
                              {gdnButtonText || 'BEGIN TOUR'}
                              <ArrowRight className="h-5 w-5" data-id="element-4289" />
                            </button>
                            <button className="mt-8 flex items-center gap-2 text-white/60 hover:text-white transition-colors" data-id="element-4290">
                              <LogOut className="h-4 w-4" data-id="element-4291" />
                              <span className="text-sm font-medium" data-id="element-4292">
                                {gdnExitText || 'Exit'}
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* EDITOR MODE */}
                      <div className={`absolute inset-0 bg-slate-50 flex flex-col transition-opacity duration-300 overflow-y-auto p-6 space-y-6 ${gdnViewMode === 'editor' ? 'opacity-100 z-20' : 'opacity-0 z-0 pointer-events-none'}`} data-id="element-4293">
                        <Card className="p-8 space-y-8" data-id="element-4294">
                          <div className="border-b border-slate-100 pb-6" data-id="element-4295">
                            <h3 className="text-lg font-semibold text-slate-900" data-id="element-4296">
                              GDN Launcher
                            </h3>
                            <p className="text-sm text-slate-500 mt-1" data-id="element-4297">
                              Customize GDN Launcher appearance
                            </p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4298">
                            <div data-id="element-4299">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4300">
                                Title
                              </label>
                              <input type="text" value={gdnTitle} onChange={e => {
                          setGdnTitle(e.target.value);
                          setHasChanges(true);
                        }} placeholder="Launcher title" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4301" />
                            </div>
                            <div data-id="element-4302">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4303">
                                Tour Redirect Type
                              </label>
                              <select defaultValue="magic-link" onChange={() => setHasChanges(true)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 bg-white" data-id="element-4304">
                                <option value="magic-link" data-id="element-4305">Magic Link</option>
                                <option value="direct" data-id="element-4306">Direct</option>
                                <option value="qr-code" data-id="element-4307">QR Code</option>
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4308">
                            <div data-id="element-4309">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4310">
                                Homepage URL (Exit Redirection)
                              </label>
                              <input type="url" defaultValue="" onChange={() => setHasChanges(true)} placeholder="https://example.com" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4311" />
                            </div>
                            <div data-id="element-4312">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4313">
                                Inactivity Timeout (s)
                              </label>
                              <input type="number" defaultValue={180} onChange={() => setHasChanges(true)} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900" data-id="element-4314" />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4315">
                            <div data-id="element-4316">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4317">
                                Button Text
                              </label>
                              <input type="text" value={gdnButtonText} onChange={e => {
                          setGdnButtonText(e.target.value);
                          setHasChanges(true);
                        }} placeholder="Enter button text" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4318" />
                            </div>
                            <div data-id="element-4319">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4320">
                                Exit Text
                              </label>
                              <input type="text" value={gdnExitText} onChange={e => {
                          setGdnExitText(e.target.value);
                          setHasChanges(true);
                        }} placeholder="Enter exit text" className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400" data-id="element-4321" />
                            </div>
                          </div>
                        </Card>

                        <Card className="p-8 space-y-8" data-id="element-4322">
                          <div className="border-b border-slate-100 pb-6" data-id="element-4323">
                            <h3 className="text-lg font-semibold text-slate-900" data-id="element-4324">
                              Theme Settings
                            </h3>
                            <p className="text-sm text-slate-500 mt-1" data-id="element-4325">
                              Customize the visual appearance of the GDN
                              Launcher.
                            </p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4326">
                            <div data-id="element-4327">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4328">
                                Primary Color
                              </label>
                              <div className="flex items-center gap-3" data-id="element-4329">
                                <input type="color" value={gdnPrimaryColor} onChange={e => {
                            setGdnPrimaryColor(e.target.value);
                            setHasChanges(true);
                          }} className="h-10 w-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white" data-id="element-4330" />
                                <input type="text" value={gdnPrimaryColor} onChange={e => {
                            setGdnPrimaryColor(e.target.value);
                            setHasChanges(true);
                          }} placeholder="#1e293b" className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400 font-mono text-sm" data-id="element-4331" />
                              </div>
                            </div>
                            <div data-id="element-4332">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4333">
                                Overlay Color
                              </label>
                              <div className="flex items-center gap-3" data-id="element-4334">
                                <input type="color" value={gdnOverlayColor} onChange={e => {
                            setGdnOverlayColor(e.target.value);
                            setHasChanges(true);
                          }} className="h-10 w-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white" data-id="element-4335" />
                                <input type="text" value={gdnOverlayColor} onChange={e => {
                            setGdnOverlayColor(e.target.value);
                            setHasChanges(true);
                          }} placeholder="#0f172a" className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400 font-mono text-sm" data-id="element-4336" />
                              </div>
                            </div>
                            <div data-id="element-4337">
                              <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4338">
                                On Primary Color
                              </label>
                              <div className="flex items-center gap-3" data-id="element-4339">
                                <input type="color" value={gdnOnPrimaryColor} onChange={e => {
                            setGdnOnPrimaryColor(e.target.value);
                            setHasChanges(true);
                          }} className="h-10 w-10 rounded-lg border border-slate-200 cursor-pointer p-0.5 bg-white" data-id="element-4340" />
                                <input type="text" value={gdnOnPrimaryColor} onChange={e => {
                            setGdnOnPrimaryColor(e.target.value);
                            setHasChanges(true);
                          }} placeholder="#ffffff" className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none transition-shadow text-slate-900 placeholder:text-slate-400 font-mono text-sm" data-id="element-4341" />
                              </div>
                            </div>
                          </div>
                        </Card>
                      </div>
                    </div>

                    {/* Floating Thumbnail Switcher */}
                    <div className="absolute bottom-6 right-6 z-40" data-id="element-4342">
                      <button onClick={() => setGdnViewMode(gdnViewMode === 'preview' ? 'editor' : 'preview')} className="group relative w-40 h-28 rounded-xl overflow-hidden shadow-2xl border-2 border-white/20 hover:border-primary-400 transition-all duration-300 hover:scale-105 bg-slate-100" data-id="element-4343">
                        {gdnViewMode === 'preview' ? <div className="w-full h-full bg-slate-50 p-2 flex flex-col gap-1.5" data-id="element-4344">
                            <div className="h-3 w-full bg-slate-200 rounded-sm" data-id="element-4345" />
                            <div className="flex gap-1" data-id="element-4346">
                              <div className="h-2 w-8 bg-primary-200 rounded-sm" data-id="element-4347" />
                              <div className="h-2 w-8 bg-slate-200 rounded-sm" data-id="element-4348" />
                            </div>
                            <div className="flex-1 grid grid-cols-2 gap-1 mt-1" data-id="element-4349">
                              <div className="bg-white border border-slate-200 rounded-sm shadow-sm" data-id="element-4350" />
                              <div className="bg-white border border-slate-200 rounded-sm shadow-sm" data-id="element-4351" />
                            </div>
                            <div className="absolute bottom-1.5 left-0 right-0 flex justify-center" data-id="element-4352">
                              <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider bg-white/90 px-1.5 py-0.5 rounded shadow-sm border border-slate-200" data-id="element-4353">
                                Edit
                              </span>
                            </div>
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-[2px]" data-id="element-4354">
                              <span className="text-white text-xs font-bold flex items-center gap-1" data-id="element-4355">
                                <LayoutTemplate className="h-3 w-3" data-id="element-4356" /> Switch to
                                Editor
                              </span>
                            </div>
                          </div> : <div className="w-full h-full relative bg-black" data-id="element-4357">
                            <div className="absolute inset-0 bg-cover bg-center opacity-60" style={{
                      backgroundImage: 'url("https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400")'
                    }} data-id="element-4358" />
                            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-black/80 backdrop-blur-sm border-t border-white/10" data-id="element-4359" />
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-[2px]" data-id="element-4360">
                              <span className="text-white text-xs font-bold flex items-center gap-1" data-id="element-4361">
                                <Eye className="h-3 w-3" data-id="element-4362" /> Switch to Preview
                              </span>
                            </div>
                          </div>}
                      </button>
                    </div>
                  </div>}

                {settingsSection === 'gdn-launcher-drawer' && isAdmin && <div className="space-y-6 relative h-[calc(100vh-16rem)] flex flex-col rounded-xl overflow-hidden border border-slate-200 shadow-sm" data-id="element-4363">
                    {/* FULL SCREEN PREVIEW */}
                    <div className="h-full w-full relative bg-slate-900 overflow-hidden flex items-center justify-center" data-id="element-4364">
                      <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200" alt="Background" className="absolute inset-0 w-full h-full object-cover blur-sm scale-105" data-id="element-4365" />
                      <div className="absolute inset-0" style={{
                  backgroundColor: gdnOverlayColor,
                  opacity: 0.7
                }} data-id="element-4366" />
                      <div className="absolute top-4 left-4 z-20 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10" data-id="element-4367">
                        <span className="text-xs font-semibold text-white/80 uppercase tracking-wider" data-id="element-4368">
                          Live Preview
                        </span>
                      </div>
                      <div className="absolute top-6 right-6 z-10" data-id="element-4369">
                        <div className="p-2 rounded-full border border-white/20 bg-black/20 backdrop-blur-sm" data-id="element-4370">
                          <Settings className="h-5 w-5 text-white/60" data-id="element-4371" />
                        </div>
                      </div>
                      <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-2xl px-8" data-id="element-4372">
                        <div className="h-24 w-24 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mb-10 shadow-2xl" data-id="element-4373">
                          <HomeIcon className="h-10 w-10 text-white/80" data-id="element-4374" />
                        </div>
                        <h1 className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-[0.2em] text-center mb-10 drop-shadow-lg" data-id="element-4375">
                          {gdnTitle || 'VIRTUAL EXPERIENCE'}
                        </h1>
                        <button className="flex items-center gap-3 px-8 py-4 rounded-lg font-semibold uppercase tracking-wider transition-transform hover:scale-105 shadow-xl" style={{
                    backgroundColor: gdnPrimaryColor,
                    color: gdnOnPrimaryColor
                  }} data-id="element-4376">
                          {gdnButtonText || 'BEGIN TOUR'}
                          <ArrowRight className="h-5 w-5" data-id="element-4377" />
                        </button>
                        <button className="mt-8 flex items-center gap-2 text-white/60 hover:text-white transition-colors" data-id="element-4378">
                          <LogOut className="h-4 w-4" data-id="element-4379" />
                          <span className="text-sm font-medium" data-id="element-4380">
                            {gdnExitText || 'Exit'}
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* Floating Edit Button */}
                    <AnimatePresence data-id="element-4381">
                      {!isGdnDrawerOpen && <motion.button initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.9
                }} onClick={() => setIsGdnDrawerOpen(true)} className="absolute bottom-6 right-6 z-20 bg-white text-slate-900 px-5 py-3 rounded-full font-semibold shadow-xl hover:bg-slate-100 transition-colors flex items-center gap-2 border border-slate-200" data-id="element-4382">
                          <Settings2 className="h-5 w-5" data-id="element-4383" />
                          Edit Launcher Settings
                        </motion.button>}
                    </AnimatePresence>

                    {/* SLIDE-OVER DRAWER */}
                    <AnimatePresence data-id="element-4384">
                      {isGdnDrawerOpen && <>
                          <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} onClick={() => setIsGdnDrawerOpen(false)} className="absolute inset-0 bg-black/20 backdrop-blur-sm z-30" data-id="element-4385" />
                          <motion.div initial={{
                    x: '100%'
                  }} animate={{
                    x: 0
                  }} exit={{
                    x: '100%'
                  }} transition={{
                    type: 'spring',
                    damping: 25,
                    stiffness: 200
                  }} className="absolute top-0 right-0 bottom-0 w-[360px] bg-white shadow-2xl z-40 flex flex-col border-l border-slate-200" data-id="element-4386">
                            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0 bg-slate-50" data-id="element-4387">
                              <div data-id="element-4388">
                                <div className="flex items-center gap-2 mb-0.5" data-id="element-4389">
                                  <Rocket className="h-5 w-5 text-primary-600" data-id="element-4390" />
                                  <h1 className="text-lg font-bold text-slate-900" data-id="element-4391">
                                    Launcher Settings
                                  </h1>
                                </div>
                                <p className="text-xs text-slate-500" data-id="element-4392">
                                  Changes reflect instantly in preview
                                </p>
                              </div>
                              <button onClick={() => setIsGdnDrawerOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors" data-id="element-4393">
                                <X className="h-5 w-5" data-id="element-4394" />
                              </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50 space-y-6" data-id="element-4395">
                              <Card className="p-6 space-y-6" data-id="element-4396">
                                <div className="border-b border-slate-100 pb-4" data-id="element-4397">
                                  <h3 className="text-base font-semibold text-slate-900" data-id="element-4398">
                                    GDN Launcher
                                  </h3>
                                </div>
                                <div className="space-y-4" data-id="element-4399">
                                  <div data-id="element-4400">
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4401">
                                      Title
                                    </label>
                                    <input type="text" value={gdnTitle} onChange={e => {
                              setGdnTitle(e.target.value);
                              setHasChanges(true);
                            }} placeholder="Launcher title" className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none text-sm" data-id="element-4402" />
                                  </div>
                                  <div data-id="element-4403">
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4404">
                                      Tour Redirect Type
                                    </label>
                                    <select defaultValue="magic-link" onChange={() => setHasChanges(true)} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none text-sm bg-white" data-id="element-4405">
                                      <option value="magic-link" data-id="element-4406">
                                        Magic Link
                                      </option>
                                      <option value="direct" data-id="element-4407">Direct</option>
                                      <option value="qr-code" data-id="element-4408">QR Code</option>
                                    </select>
                                  </div>
                                  <div data-id="element-4409">
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4410">
                                      Homepage URL (Exit Redirection)
                                    </label>
                                    <input type="url" defaultValue="" onChange={() => setHasChanges(true)} placeholder="https://example.com" className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none text-sm" data-id="element-4411" />
                                  </div>
                                  <div data-id="element-4412">
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4413">
                                      Inactivity Timeout (s)
                                    </label>
                                    <input type="number" defaultValue={180} onChange={() => setHasChanges(true)} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none text-sm" data-id="element-4414" />
                                  </div>
                                  <div className="grid grid-cols-2 gap-4" data-id="element-4415">
                                    <div data-id="element-4416">
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4417">
                                        Button Text
                                      </label>
                                      <input type="text" value={gdnButtonText} onChange={e => {
                                setGdnButtonText(e.target.value);
                                setHasChanges(true);
                              }} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none text-sm" data-id="element-4418" />
                                    </div>
                                    <div data-id="element-4419">
                                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4420">
                                        Exit Text
                                      </label>
                                      <input type="text" value={gdnExitText} onChange={e => {
                                setGdnExitText(e.target.value);
                                setHasChanges(true);
                              }} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none text-sm" data-id="element-4421" />
                                    </div>
                                  </div>
                                </div>
                              </Card>

                              <Card className="p-6 space-y-6" data-id="element-4422">
                                <div className="border-b border-slate-100 pb-4" data-id="element-4423">
                                  <h3 className="text-base font-semibold text-slate-900" data-id="element-4424">
                                    Theme Settings
                                  </h3>
                                </div>
                                <div className="space-y-4" data-id="element-4425">
                                  <div data-id="element-4426">
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4427">
                                      Primary Color
                                    </label>
                                    <div className="flex items-center gap-3" data-id="element-4428">
                                      <input type="color" value={gdnPrimaryColor} onChange={e => {
                                setGdnPrimaryColor(e.target.value);
                                setHasChanges(true);
                              }} className="h-8 w-8 rounded border border-slate-200 cursor-pointer p-0.5 bg-white" data-id="element-4429" />
                                      <input type="text" value={gdnPrimaryColor} onChange={e => {
                                setGdnPrimaryColor(e.target.value);
                                setHasChanges(true);
                              }} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none text-sm font-mono" data-id="element-4430" />
                                    </div>
                                  </div>
                                  <div data-id="element-4431">
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4432">
                                      Overlay Color
                                    </label>
                                    <div className="flex items-center gap-3" data-id="element-4433">
                                      <input type="color" value={gdnOverlayColor} onChange={e => {
                                setGdnOverlayColor(e.target.value);
                                setHasChanges(true);
                              }} className="h-8 w-8 rounded border border-slate-200 cursor-pointer p-0.5 bg-white" data-id="element-4434" />
                                      <input type="text" value={gdnOverlayColor} onChange={e => {
                                setGdnOverlayColor(e.target.value);
                                setHasChanges(true);
                              }} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none text-sm font-mono" data-id="element-4435" />
                                    </div>
                                  </div>
                                  <div data-id="element-4436">
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4437">
                                      On Primary Color
                                    </label>
                                    <div className="flex items-center gap-3" data-id="element-4438">
                                      <input type="color" value={gdnOnPrimaryColor} onChange={e => {
                                setGdnOnPrimaryColor(e.target.value);
                                setHasChanges(true);
                              }} className="h-8 w-8 rounded border border-slate-200 cursor-pointer p-0.5 bg-white" data-id="element-4439" />
                                      <input type="text" value={gdnOnPrimaryColor} onChange={e => {
                                setGdnOnPrimaryColor(e.target.value);
                                setHasChanges(true);
                              }} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 outline-none text-sm font-mono" data-id="element-4440" />
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            </div>
                          </motion.div>
                        </>}
                    </AnimatePresence>
                  </div>}
              </div>
            </div>
          </div>}
      </div>

      {/* Edit Model Modal */}
      {isModalOpen && editingModel && <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-4441">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full my-8 animate-in fade-in zoom-in-95 duration-200" data-id="element-4442">
            <div className="p-6 border-b border-slate-100 flex items-start justify-between sticky top-0 bg-white rounded-t-xl z-10" data-id="element-4443">
              <div className="flex items-start gap-4 min-w-0" data-id="element-4444">
                {editingModel.hasImage && editingModel.imageUrl ? <img src={editingModel.imageUrl} alt={editingModel.name} className="h-16 w-16 rounded-lg object-cover border border-slate-200 flex-shrink-0" data-id="element-4445" /> : <div className="h-16 w-16 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0" data-id="element-4446">
                    <HomeIcon className="h-6 w-6 text-slate-300" data-id="element-4447" />
                  </div>}
                <div className="min-w-0" data-id="element-4448">
                  <h2 className="text-xl font-bold text-slate-900" data-id="element-4449">
                    {editingModel.id.startsWith('model-') && !editingModel.name ? 'Add Model' : 'Update Model'}
                  </h2>
                  <p className="text-sm text-slate-500 mt-0.5" data-id="element-4450">
                    Update the details of the model below.
                  </p>
                  {editingModel.name && <p className="text-xs text-slate-400 mt-1 font-mono truncate" data-id="element-4451">
                      ID: {editingModel.id}
                    </p>}
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-100 rounded-lg flex-shrink-0" data-id="element-4452">
                <X className="h-5 w-5" data-id="element-4453" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto" data-id="element-4454">
              {/* Toggles row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4455">
                <div data-id="element-4456">
                  <div className="flex items-center justify-between gap-3" data-id="element-4457">
                    <span className="text-sm font-semibold text-slate-900" data-id="element-4458">
                      Enabled
                    </span>
                    <Toggle checked={editingModel.enabled ?? true} onChange={v => setEditingModel({
                  ...editingModel,
                  enabled: v
                })} data-id="element-4459" />
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5" data-id="element-4460">
                    Toggles model visibility in the listing page
                  </p>
                </div>
                <div data-id="element-4461">
                  <div className="flex items-center justify-between gap-3" data-id="element-4462">
                    <span className="text-sm font-semibold text-slate-900" data-id="element-4463">
                      Display Product Prices
                    </span>
                    <Toggle checked={editingModel.displayProductPrices ?? false} onChange={v => setEditingModel({
                  ...editingModel,
                  displayProductPrices: v
                })} data-id="element-4464" />
                  </div>
                  <p className="text-xs text-slate-500 mt-1.5" data-id="element-4465">
                    Affects prices under individual catalog options and the
                    shopping cart
                  </p>
                </div>
              </div>

              {/* Model Name | Home Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4466">
                <div data-id="element-4467">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4468">
                    Model Name <span className="text-red-500" data-id="element-4469">*</span>
                  </label>
                  <input type="text" value={editingModel.name} onChange={e => setEditingModel({
                ...editingModel,
                name: e.target.value
              })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-4470" />
                </div>
                <div data-id="element-4471">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4472">
                    Home Type
                  </label>
                  <select value={editingModel.type} onChange={e => setEditingModel({
                ...editingModel,
                type: e.target.value
              })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white" data-id="element-4473">
                    <option value="Laned Home" data-id="element-4474">Laned Home</option>
                    <option value="Front Garage" data-id="element-4475">Front Garage</option>
                    <option value="Bungalow" data-id="element-4476">Bungalow</option>
                    <option value="Estate" data-id="element-4477">Estate</option>
                    <option value="Townhome" data-id="element-4478">Townhome</option>
                    <option value="Duplex" data-id="element-4479">Duplex</option>
                  </select>
                </div>
              </div>

              {/* Status | Base Price */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4480">
                <div data-id="element-4481">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4482">
                    Status
                  </label>
                  <select value={editingModel.status} onChange={e => setEditingModel({
                ...editingModel,
                status: e.target.value as any
              })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white" data-id="element-4483">
                    <option value="active" data-id="element-4484">Active</option>
                    <option value="draft" data-id="element-4485">Draft</option>
                    <option value="archived" data-id="element-4486">Archived</option>
                  </select>
                </div>
                <div data-id="element-4487">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4488">
                    Base Price
                  </label>
                  <div className="relative" data-id="element-4489">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-4490" />
                    <input type="number" value={editingModel.basePrice} onChange={e => setEditingModel({
                  ...editingModel,
                  basePrice: Number(e.target.value)
                })} className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-4491" />
                  </div>
                </div>
              </div>

              {/* Beds | Baths | Size */}
              <div className="grid grid-cols-3 gap-4" data-id="element-4492">
                <div data-id="element-4493">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4494">
                    Beds
                  </label>
                  <input type="number" value={editingModel.beds} onChange={e => setEditingModel({
                ...editingModel,
                beds: Number(e.target.value)
              })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-4495" />
                </div>
                <div data-id="element-4496">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4497">
                    Baths
                  </label>
                  <input type="number" step="0.5" value={editingModel.baths} onChange={e => setEditingModel({
                ...editingModel,
                baths: Number(e.target.value)
              })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-4498" />
                </div>
                <div data-id="element-4499">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4500">
                    Size (Sq. Ft. or M²)
                  </label>
                  <input type="number" value={editingModel.sqft} onChange={e => setEditingModel({
                ...editingModel,
                sqft: Number(e.target.value)
              })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-4501" />
                </div>
              </div>

              {/* Location */}
              <div data-id="element-4502">
                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4503">
                  Location
                </label>
                <div className="relative" data-id="element-4504">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-4505" />
                  <input type="text" value={editingModel.location} onChange={e => setEditingModel({
                ...editingModel,
                location: e.target.value
              })} placeholder="e.g. Roseville, CA" className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-4506" />
                </div>
              </div>

              {/* UE Asset Path */}
              <div data-id="element-4507">
                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4508">
                  UE Asset Path
                </label>
                <input type="text" value={editingModel.ueAssetPath || ''} onChange={e => setEditingModel({
              ...editingModel,
              ueAssetPath: e.target.value
            })} placeholder="/Game/z_Projects/…" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none font-mono text-sm" data-id="element-4509" />
              </div>

              {/* UE Level Name */}
              <div data-id="element-4510">
                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4511">
                  UE Level Name
                </label>
                <input type="text" value={editingModel.ueLevelName || ''} onChange={e => setEditingModel({
              ...editingModel,
              ueLevelName: e.target.value
            })} placeholder="Map_…" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none font-mono text-sm" data-id="element-4512" />
              </div>

              {/* Rooms repeater */}
              <div className="pt-2 border-t border-slate-100" data-id="element-4513">
                <div className="flex items-center justify-between mb-1" data-id="element-4514">
                  <h3 className="text-base font-bold text-slate-900" data-id="element-4515">Rooms</h3>
                  <span className="text-sm text-slate-500" data-id="element-4516">
                    {(editingModel.rooms || []).length}{' '}
                    {(editingModel.rooms || []).length === 1 ? 'room' : 'rooms'}
                  </span>
                </div>
                <p className="text-sm text-slate-500 mb-4" data-id="element-4517">
                  Define the rooms that make up this model.
                </p>

                <div className="space-y-3" data-id="element-4518">
                  {(editingModel.rooms || []).map((room, idx) => <div key={room.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50/60" data-id="element-4519">
                      <div className="flex items-center justify-between mb-3" data-id="element-4520">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-4521">
                          Room {idx + 1}
                        </span>
                        <button onClick={() => handleRemoveRoom(room.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1 rounded hover:bg-white" aria-label="Remove room" data-id="element-4522">
                          <Trash2 className="h-4 w-4" data-id="element-4523" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4524">
                        <div data-id="element-4525">
                          <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-4526">
                            Room Name
                          </label>
                          <input type="text" value={room.name} onChange={e => handleUpdateRoom(room.id, {
                      name: e.target.value
                    })} placeholder="e.g. Primary Bedroom" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white" data-id="element-4527" />
                        </div>
                        <div data-id="element-4528">
                          <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-4529">
                            Square Footage
                          </label>
                          <div className="relative" data-id="element-4530">
                            <Ruler className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" data-id="element-4531" />
                            <input type="number" value={room.sqft} onChange={e => handleUpdateRoom(room.id, {
                        sqft: Number(e.target.value)
                      })} className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white" data-id="element-4532" />
                          </div>
                        </div>
                        <div data-id="element-4533">
                          <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-4534">
                            Room Type
                          </label>
                          <select value={room.type} onChange={e => handleUpdateRoom(room.id, {
                      type: e.target.value
                    })} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white" data-id="element-4535">
                            <option value="Bedroom" data-id="element-4536">Bedroom</option>
                            <option value="Bathroom" data-id="element-4537">Bathroom</option>
                            <option value="Kitchen" data-id="element-4538">Kitchen</option>
                            <option value="Living Room" data-id="element-4539">Living Room</option>
                            <option value="Dining Room" data-id="element-4540">Dining Room</option>
                            <option value="Office" data-id="element-4541">Office</option>
                            <option value="Garage" data-id="element-4542">Garage</option>
                            <option value="Basement" data-id="element-4543">Basement</option>
                            <option value="Laundry" data-id="element-4544">Laundry</option>
                            <option value="Other" data-id="element-4545">Other</option>
                          </select>
                        </div>
                        <div data-id="element-4546">
                          <label className="block text-xs font-medium text-slate-600 mb-1" data-id="element-4547">
                            Floor
                          </label>
                          <select value={room.floor} onChange={e => handleUpdateRoom(room.id, {
                      floor: e.target.value
                    })} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none bg-white" data-id="element-4548">
                            <option value="Basement" data-id="element-4549">Basement</option>
                            <option value="Main" data-id="element-4550">Main</option>
                            <option value="Upper" data-id="element-4551">Upper</option>
                            <option value="Loft" data-id="element-4552">Loft</option>
                          </select>
                        </div>
                      </div>
                    </div>)}
                </div>

                {(editingModel.rooms || []).length === 0 && <div className="text-center py-6 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-xl" data-id="element-4553">
                    No rooms added yet
                  </div>}

                <button onClick={handleAddRoom} className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-slate-300 text-slate-500 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50/30 transition-all text-sm font-medium" data-id="element-4554">
                  <Plus className="h-4 w-4" data-id="element-4555" />
                  Add Room
                </button>
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3 rounded-b-xl sticky bottom-0 z-10" data-id="element-4556">
              <Button variant="outline" onClick={() => setIsModalOpen(false)} data-id="element-4557">
                Cancel
              </Button>
              <Button onClick={handleSaveModel} disabled={!editingModel.name} data-id="element-4558">
                Update Model
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}